import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "modal__container modal__container--base-data" }
const _hoisted_2 = { class: "modal__container modal__container--amount" }

import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { computed } from 'vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import { ref, watch } from 'vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import {
  formOptionsAggregateBusiness,
  inputTypeKeyMap,
  validateRuleKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  unitOptionsMapping,
  formOptionsAggregateBusinessUniqueKeys,
} from '../constants';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import { convertLeadTimeByUnit, convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { useUpdateFieldState } from '../composables/useUpdateFieldState';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';


export default /*@__PURE__*/_defineComponent({
  __name: 'UpdateAggregateBusinessModal',
  props: {
    item: { default: undefined }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);

const { getAddableOptions } = store;
const props = __props;

const emits = __emit;

const isEdited = ref<boolean>(false);

const fieldItems = computed((): FieldItem[] => {
  const targetArray = formOptionsAggregateBusiness;
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

const currentOpenSelectInputName = ref('');

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (allValidate()) {
    const aggregateBusinessResponsibleUsersString = fieldState['aggregateBusinessResponsibleUser'].value.value;
    const convertedLeadTimeUnit = updateBusinessVM.convertDateUnit(fieldState['leadTimeUnit'].value.value);
    const updatedItem = BusinessFactory.create({
      ...props.item,
      warehouseNumber: fieldState['warehouseNumber'].value.value,
      warehouse: fieldState['warehouseName'].value.value,
      supplierNumber: fieldState['supplierNumber'].value.value,
      supplierName: fieldState['supplierName'].value.value,
      businessNumber: fieldState['businessNumber'].value.value,
      businessName: fieldState['businessName'].value.value,
      package: fieldState['package'].value.value,
      unit: fieldState['unit'].value.value,
      manufacturer: updateBusinessVM.convertUnassingedToUndefined(fieldState['manufacturer'].value.value),
      leadTime: convertLeadTimeToDays(Number(fieldState['leadTime'].value.value) || 0, convertedLeadTimeUnit),
      leadTimeUnit: convertedLeadTimeUnit,
      MOQ: Number(fieldState['MOQ'].value.value) || 0,
      orderPackage: Number(fieldState['orderPackage'].value.value) || 0,
      stdInventoryIndices: {
        標準在庫指数: {
          max: Number(fieldState['stdInventoryIndicesMax'].value.value) || 0,
          min: Number(fieldState['stdInventoryIndicesMin'].value.value) || 0,
          option: {
            unit: updateBusinessVM.convertDateUnit(fieldState['stdInventoryIndicesUnit'].value.value),
          },
        },
      },
      userUniqueNumber: fieldState.userUniqueNumber.value.value,
      aggregateBusinessResponsibleUsers:
        aggregateBusinessResponsibleUsersString.length > 0
          ? aggregateBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
          : [],
    });
    const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
    state.value.updatedItemDiff = updatedDiff.first;

    state.value.item = updatedItem;
    emits('close', 'aggregateBusinessEdit', true);
  }
};

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  return [formOptionsAggregateBusiness.slice(0, 11), formOptionsAggregateBusiness.slice(11)];
});

/**
 * モーダルを開いた際の処理
 */
watch(
  () => state.value.modalState.aggregateBusinessEdit,
  (newVal) => {
    if (newVal && props.item) {
      const targetLeadTimeUnit = state.value.item?.['leadTimeUnit'] ?? props.item['leadTimeUnit'];
      const targetStdInventoryIndices = state.value.item?.stdInventoryIndices ?? props.item.stdInventoryIndices;
      resetForm(fieldItems.value);
      for (let i = 0; i < fieldItems.value.length; i++) {
        const keyName = fieldItems.value[i].name;
        switch (keyName) {
          case 'userUniqueNumber':
            fieldState[keyName].setValue(props.item.userUniqueNumber ?? '', false);
            break;

          case 'warehouseName':
            fieldState[keyName].setValue(state.value.item?.warehouse ?? props.item.warehouse, false);
            break;

          case 'leadTime':
            fieldState[keyName].setValue(
              convertLeadTimeByUnit(state.value.item?.leadTime ?? props.item.leadTime ?? 0, targetLeadTimeUnit ?? '').toString()
            );
            break;

          case 'leadTimeUnit':
            fieldState[keyName].setValue(targetLeadTimeUnit ? t(unitOptionsMapping[targetLeadTimeUnit]) : '', false);
            break;

          case 'stdInventoryIndicesMin':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.min ? targetStdInventoryIndices['標準在庫指数'].min.toString() : '',
              false
            );
            break;

          case 'stdInventoryIndicesMax':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.max ? targetStdInventoryIndices['標準在庫指数'].max.toString() : '',
              false
            );
            break;

          case 'stdInventoryIndicesUnit':
            fieldState[keyName].setValue(
              targetStdInventoryIndices?.標準在庫指数?.option.unit
                ? t(unitOptionsMapping[targetStdInventoryIndices['標準在庫指数'].option.unit])
                : '',
              false
            );
            break;

          case 'aggregateBusinessResponsibleUser':
            fieldState[keyName].setValue(
              state.value.item?.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
                props.item.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
                '',
              false
            );
            break;

          default:
            fieldState[keyName].setValue(state.value.item?.[keyName] ?? props.item[keyName] ?? '', false);
        }
      }
    }
  }
);

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 * @param index - インデックス
 */
const getBaseDataItemClass = (option: string, index: number) => {
  if (index < 5) {
    return 'modal__item--base-data--upper';
  } else if (option === 'package' || option === 'unit') {
    return 'modal__item--base-data--number';
  } else {
    return 'modal__item--base-data--lower';
  }
};

const modalText = computed<{ title: string; desc: string }>(() => {
  if (state.value.isCreatingBoth) {
    return {
      title: t('追加する集計取引情報'),
      desc: t('関連取引と同時に追加する集計取引情報を入力してください。'),
    };
  } else if (state.value.isCreateMode) {
    return {
      title: t('取引追加'),
      desc: t('新規に取引を追加できます。'),
    };
  } else {
    return {
      title: t('取引編集'),
      desc: t('選択済の取引ファイルに対して、項目の内容を修正できます。'),
    };
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": _unref(state).modalState.aggregateBusinessEdit,
    "modal-width": 1300,
    "back-button-label": _unref(t)('キャンセル'),
    "next-button-label": _unref(t)('保存'),
    "is-disabled-next-button": !isEdited.value,
    onOnBack: _cache[0] || (_cache[0] = 
      () => {
        emits('close', 'aggregateBusinessEdit', false);
      }
    ),
    onOnNext: editingModalOnNext
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(modalText.value.title), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(modalText.value.desc), 1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDividedFormOptions.value[0], (option, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: option,
            class: _normalizeClass(["modal__item", getBaseDataItemClass(option, index)])
          }, [
            (_unref(inputTypeKeyMap)[option] === 'select')
              ? (_openBlock(), _createBlock(SingleSelectInput, {
                  key: 0,
                  index: index,
                  label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                  "model-value": _unref(fieldState)[option].value.value,
                  placeholder: _unref(t)(option),
                  "filter-type": _unref(keyMappingForSelectInput)[option],
                  "is-error": _unref(fieldState)[option]?.isError.value,
                  "error-message": _unref(fieldState)[option]?.errorMessage.value,
                  disabled: _unref(state).isCreatingBoth && _unref(formOptionsAggregateBusinessUniqueKeys).includes(option),
                  "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                  "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "disabled", "onClick:selectInput", "onUpdate:modelValue"]))
              : (_unref(inputTypeKeyMap)[option] === 'selectUser')
                ? (_openBlock(), _createBlock(MultipleUserSelectInput, {
                    key: 1,
                    "select-input-index": index,
                    label: _unref(t)(option),
                    placeholder: _unref(t)(option),
                    "original-selected-user-id-list": _unref(fieldState)[option].value.value ? _unref(fieldState)[option].value.value.split(',') : [],
                    "onUpdate:modelValue": (e) => _unref(updateUserValue)(option, e)
                  }, null, 8, ["select-input-index", "label", "placeholder", "original-selected-user-id-list", "onUpdate:modelValue"]))
                : (_openBlock(), _createBlock(TextInput, {
                    key: 2,
                    "model-value": _unref(fieldState)[option].value.value,
                    label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                    placeholder: _unref(t)(option),
                    "is-error": _unref(fieldState)[option].isError.value,
                    "error-message": _unref(t)(_unref(fieldState)[option].errorMessage.value),
                    "ensure-error-msg-height": "",
                    "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                  }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "onUpdate:modelValue"]))
          ], 2))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDividedFormOptions.value[1], (option, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: option,
            class: "modal__item"
          }, [
            (_unref(inputTypeKeyMap)[option] === 'select')
              ? (_openBlock(), _createBlock(SingleSelectInput, {
                  key: 0,
                  index: index + getDividedFormOptions.value[0].length,
                  label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                  "model-value": _unref(fieldState)[option].value.value,
                  placeholder: _unref(t)(option),
                  "filter-type": _unref(keyMappingForSelectInput)[option],
                  "is-error": _unref(fieldState)[option]?.isError.value,
                  "error-message": _unref(fieldState)[option]?.errorMessage.value,
                  "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                  "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "onClick:selectInput", "onUpdate:modelValue"]))
              : _createCommentVNode("", true),
            (_unref(inputTypeKeyMap)[option] === 'selectUnit')
              ? (_openBlock(), _createBlock(SingleSelectInput, {
                  key: 1,
                  index: index + getDividedFormOptions.value[0].length,
                  label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                  "model-value": _unref(fieldState)[option].value.value,
                  placeholder: _unref(t)(option),
                  "filter-type": _unref(keyMappingForSelectInput)[option],
                  "is-error": _unref(fieldState)[option]?.isError.value,
                  "error-message": _unref(fieldState)[option]?.errorMessage.value,
                  "prop-options": _unref(unitOptions).map((o) => _unref(t)(o)),
                  "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                  "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "prop-options", "onClick:selectInput", "onUpdate:modelValue"]))
              : (_openBlock(), _createBlock(TextInput, {
                  key: 2,
                  "model-value": _unref(fieldState)[option].value.value,
                  label: option === 'MOQ' ? _unref(t)('取引編集MOQ') : _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                  placeholder: _unref(t)(option),
                  "is-error": _unref(fieldState)[option].isError.value,
                  "error-message": _unref(t)(_unref(fieldState)[option].errorMessage.value),
                  "ensure-error-msg-height": "",
                  "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "onUpdate:modelValue"]))
          ]))
        }), 128))
      ]),
      _createVNode(AddOptionSection, {
        options: _unref(getAddableOptions)(true),
        onSetOption: _unref(addNewOption)
      }, null, 8, ["options", "onSetOption"])
    ]),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label", "is-disabled-next-button"]))
}
}

})